import { TerpecaRoom } from '../models/room.model';

// Combined games: a room that is two or more of our other rooms booked and played as a single playthrough,
// like the Full Experience (The Magician of Paris + The Funhouse). The combined room lists its parts in partIds;
// the parts don't point back. We may not have every part (Remigio vs Trump has only Escape from Trump), so
// "listed parts" means the parts present in the room map at hand (this year's finalists in the voting flow).
// Shared by step 3 of the voting flow and the partner played-rooms import.

export enum CombinedPrompt {
  ALL_PARTS = 'allParts',  // every listed part (two or more) is marked: ask firmly, since playing them separately doesn't count
  ONE_PART = 'onePart',    // the only listed part is marked: ask gently whether they played it as part of the combined game
}

export function roomDisplayName(room: TerpecaRoom): string {
  return room.englishName && room.englishName !== room.name ? `${room.englishName} [${room.name}]` : room.name;
}

export function listedParts(combined: TerpecaRoom, roomMap: Map<string, TerpecaRoom>): TerpecaRoom[] {
  return (combined.partIds || []).map(id => roomMap.get(id)).filter(room => Boolean(room));
}

export function combinedRoomsWithPart(partId: string, roomMap: Map<string, TerpecaRoom>): TerpecaRoom[] {
  return Array.from(roomMap.values()).filter(room => room.partIds?.includes(partId));
}

// Whether marking (or importing) the parts in isPlayed should prompt about the combined room. Callers check that the
// combined room itself isn't already played.
export function combinedPrompt(combined: TerpecaRoom, roomMap: Map<string, TerpecaRoom>,
                               isPlayed: (roomId: string) => boolean): CombinedPrompt | null {
  const parts = listedParts(combined, roomMap);
  if (!parts.length || !parts.every(part => isPlayed(part.docId))) {
    return null;
  }
  return parts.length === 1 ? CombinedPrompt.ONE_PART : CombinedPrompt.ALL_PARTS;
}

// Partner import: a definite combined play makes its listed parts definite, and a combined room whose listed parts all
// came back (definite or maybe, per combinedPrompt) becomes a maybe. A maybe combined play leaves its parts alone. Adds to definiteIds in
// place and returns the new combined maybes with their prompt, skipping rooms already definite or maybe.
export function applyCombinedImportRules(definiteIds: Set<string>, maybeIds: Set<string>,
                                         roomMap: Map<string, TerpecaRoom>): Map<string, CombinedPrompt> {
  for (const id of Array.from(definiteIds)) {
    for (const part of listedParts(roomMap.get(id) || <TerpecaRoom>{}, roomMap)) {
      definiteIds.add(part.docId);
    }
  }
  const combinedMaybes = new Map<string, CombinedPrompt>();
  for (const room of roomMap.values()) {
    if (room.partIds && !definiteIds.has(room.docId) && !maybeIds.has(room.docId)) {
      const prompt = combinedPrompt(room, roomMap, id => definiteIds.has(id) || maybeIds.has(id));
      if (prompt) {
        combinedMaybes.set(room.docId, prompt);
      }
    }
  }
  return combinedMaybes;
}
